import { Fragment, useEffect, useState } from 'react';
import { Skeleton, useMediaQuery, Box } from '@mui/material';
import { FilterConfig } from '@sm-dashboard/types';
import { FilterInput } from './FilterInput';
import { useTheme } from '@mui/material/styles';

interface FiltersContentProps<T> {
  filters: FilterConfig<T>[] | undefined;
  handleApplyFilters: (filter: FilterConfig<any>, value: any) => void;
  isPrimary?: boolean;
}

export const FiltersContent = <T,>({ filters, handleApplyFilters, isPrimary }: FiltersContentProps<T>) => {
  const [loadingFilters, setLoadingFilters] = useState(true);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (filters) {
      setLoadingFilters(false);
    }
  }, [filters]);

  // Group filters by their group property
  const groupFilters = (filters: FilterConfig<T>[] | undefined) => {
    if (!filters) return {};

    const grouped: { [key: number]: FilterConfig<T>[] } = {};

    filters.forEach(filter => {
      const group = filter.group || 0; // Default to group 0 if no group specified
      if (!grouped[group]) {
        grouped[group] = [];
      }
      grouped[group].push(filter);
    });

    return grouped;
  };

  const groupedFilters = groupFilters(filters);

  // Mobile-only: pair consecutive date filters side-by-side without affecting desktop
  const renderMobileGroup = (filtersInGroup: FilterConfig<T>[], isPrimary?: boolean) => {
    const elements: JSX.Element[] = [];
    for (let i = 0; i < filtersInGroup.length; i++) {
      const current = filtersInGroup[i];
      const next = filtersInGroup[i + 1];
      if (current?.type === 'date' && next?.type === 'date') {
        elements.push(
          <Box key={`pair-${i}`} sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <FilterInput filter={current} handleApplyFilters={handleApplyFilters} isPrimary={isPrimary} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <FilterInput filter={next} handleApplyFilters={handleApplyFilters} isPrimary={isPrimary} />
            </Box>
          </Box>
        );
        i++;
      } else {
        elements.push(
          <Fragment key={i}>
            <FilterInput filter={current} handleApplyFilters={handleApplyFilters} isPrimary={isPrimary} />
          </Fragment>
        );
      }
    }
    return elements;
  };

  if (isPrimary) {
    return (
      <Fragment>
        {Object.entries(groupedFilters).map(([groupKey, groupFilters]) =>
          isSmallScreen ? (
            <Fragment key={groupKey}>{renderMobileGroup(groupFilters, isPrimary)}</Fragment>
          ) : (
            <Fragment key={groupKey}>
              {groupFilters.map((filter: FilterConfig<any>, index) => (
                <Fragment key={index}>
                  <FilterInput filter={filter} handleApplyFilters={handleApplyFilters} isPrimary={isPrimary} />
                </Fragment>
              ))}
            </Fragment>
          )
        )}
      </Fragment>
    );
  }

  return (
    <>
      {!loadingFilters ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '18px', width: '100%' }}>
          {Object.entries(groupedFilters).map(([groupKey, groupFilters]) => (
              <Box
                key={groupKey}
                sx={{
                  display: 'flex',
                  flexDirection: isSmallScreen ? 'column' : 'row',
                  flexWrap: 'wrap',
                  gap: '12px',
                  alignItems: isSmallScreen ? 'stretch' : 'center',
                  width: isSmallScreen ? '100%' : undefined
                }}
              >
                {isSmallScreen
                  ? renderMobileGroup(groupFilters)
                  : groupFilters.map((filter: FilterConfig<any>, index) => (
                      <Fragment key={index}>
                        <FilterInput filter={filter} handleApplyFilters={handleApplyFilters} />
                      </Fragment>
                    ))}
              </Box>
          ))}
        </Box>
      ) : (
        <Skeleton variant="rectangular" width="100%" height={40} />
      )}
      {/* <Button
        variant="contained"
        color="secondary"
        size="medium"
        endIcon={!isFiltered ? <FilterAltOutlinedIcon /> : <FilterAltOffOutlinedIcon />}
        onClick={() => handleApplyFilters()}
        sx={{ color: 'white', minWidth: 90, boxShadow: 'none' }}
      >
        {isFiltered ? 'בטל' : 'סנן'}
      </Button> */}
      {/* {isSmallScreen && (
        <Button variant="outlined" onClick={() => setOpen(false)}>
          סגור
        </Button>
      )} */}
    </>
  );
};
